import React, { useMemo } from "react";
import { Pressable } from "react-native";

import {
  type OrderDiscountFragment,
  formatPrice,
  getVATByCountryCode,
  useGetVirtualWalletBalanceQuery,
} from "@stepapp/core";
import { OrderDiscountType, V2CheckoutDiscount } from "@stepapp/core/apiTypes";
import { Block, Button, LinePlaceholder, SvgIcon, Typography } from "@stepapp/ui";
import { useTranslation } from "react-i18next";
import { AppStorage } from "../services/AppStorage";
import StepappWallet from "./StepappWallet";

type Props = {
  discounts?: (OrderDiscountFragment | V2CheckoutDiscount | null)[] | null;
  currency?: string;
  couponName?: string;
  price?: number;
  total?: number;
  loading: boolean;
  onCouponPress: () => void;
  handleFocusInput: () => void;
  isLogged: boolean;
};

const TotalAndCoupon = ({
  currency,
  discounts = [],
  handleFocusInput,
  isLogged,
  loading,
  onCouponPress,
  price = 0,
  total = 0,
  couponName = "",
}: Props) => {
  const { t } = useTranslation();
  const { data: virtualWalletBalance } = useGetVirtualWalletBalanceQuery({ skip: !isLogged });
  const countryCode = AppStorage().get("countryCode");
  const priceFormatter = formatPrice(countryCode);
  const vat = getVATByCountryCode(countryCode);

  const { coupon, filteredDiscounts } = useMemo(() => {
    const filteredDiscounts = discounts?.filter(
      (d) => d?.fixedValue && d?.type !== OrderDiscountType.VirtualWallet,
    ) as OrderDiscountFragment[];
    const coupon = filteredDiscounts.find(({ type }) => type === OrderDiscountType.Coupon);
    return { coupon, filteredDiscounts };
  }, [discounts]);

  return (
    <Block marginVertical={2}>
      <Block marginBottom={1}>
        <Block justifySpace row marginTop={1}>
          <Typography variant="bodyBold">{t("common.together")}</Typography>

          {loading ? (
            <LinePlaceholder height={3} width={15} />
          ) : (
            <Typography variant="body">
              {priceFormatter(total, { includeRest: true, currency })}
            </Typography>
          )}
        </Block>
        {filteredDiscounts?.map(({ fixedValue, id, type }) => (
          <Block justifySpace row key={id} marginTop={0.5}>
            <Typography variant="bodyBold">
              {type === OrderDiscountType.Coupon
                ? t("screens.serviceSummary.coupon", { coupon: couponName })
                : type === OrderDiscountType.Subscription
                  ? t("screens.serviceSummary.subscriptionDiscount")
                  : t("pages.serviceSummary.yourStepappWallet")}
            </Typography>
            <Typography type="success" variant="body">
              -{priceFormatter(fixedValue, { includeRest: true, currency })}
            </Typography>
          </Block>
        ))}
      </Block>

      <Block alignCenter justifySpace row marginVertical={1}>
        <Typography variant="newH3">{t("screens.serviceSummary.toPay")}</Typography>
        {loading ? (
          <LinePlaceholder height={5} width={20} />
        ) : (
          <Typography variant="newH1">
            {priceFormatter(price, { includeRest: true, currency })}
          </Typography>
        )}
      </Block>

      <Typography type="secondary" variant="lightLabel">
        {t("screens.serviceSummary.grossPriceIncludingVAT", { vat })}
      </Typography>

      {isLogged ? (
        <>
          {!!virtualWalletBalance?.getVirtualWalletBalance?.balance && (
            <StepappWallet balance={virtualWalletBalance?.getVirtualWalletBalance?.balance || 0} />
          )}

          <Block justifyEnd row marginTop={2}>
            <Button
              label={
                coupon?.id
                  ? t("screens.serviceSummary.changeCoupon")
                  : t("pages.serviceSummary.enterCoupon")
              }
              leftComponent={() => (
                <Block paddingRight={1.25}>
                  <SvgIcon name="coupon" size={26} />
                </Block>
              )}
              onPress={onCouponPress}
              type="secondary"
              variant="small"
            />
          </Block>
        </>
      ) : (
        <Block row marginTop={1.5}>
          <Typography type="secondary" variant="body">
            {t("screens.serviceSummary.wantEnterCoupon")}
          </Typography>
          <Pressable onPress={() => handleFocusInput()}>
            <Typography type="accent" variant="body">
              {" "}
              {t("screens.auth.logIn")}
            </Typography>
          </Pressable>
        </Block>
      )}
    </Block>
  );
};

export default React.memo(TotalAndCoupon);
